import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { QCStandardsListResult } from "@/modules/qc/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  ClipboardCheckIcon,
  CopyIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  TrashIcon,
} from "lucide-react";

type QCRow = QCStandardsListResult["data"][number];

type QCTableProps = {
  data: QCRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit?: (qc: QCRow) => void;
  onView?: (id: string) => void;
  onCopy?: (id: string) => void;
  onActivate?: (qc: QCRow) => void;
  onDeactivate?: (qc: QCRow) => void;
  onDelete?: (qc: QCRow) => void;
  onRecordClick?: (qc: QCRow) => void;
};

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  draft: "ປັບປຸງ",
  inactive: "ເລີກໃຊ້",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  active: "default",
  draft: "secondary",
  inactive: "destructive",
};

export function QCTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onView,
  onCopy,
  onActivate,
  onDeactivate,
  onDelete,
  onRecordClick,
}: QCTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<QCRow>[] = [
    createSortableColumn<QCRow>("name", "ຊື່ມາດຕະຖານ QC", { size: 200 }),
    {
      id: "workTypeName",
      header: "ປະເພດງານ",
      cell: ({ row }) => (
        <Badge variant="secondary">{row.original.workTypeName || "-"}</Badge>
      ),
      size: 150,
    },
    {
      id: "departmentName",
      header: "ພະແນກທີ່ກ່ຽວຂ້ອງ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.departmentName || "-"}</span>
      ),
      size: 150,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={statusColors[row.original.status] || "secondary"}>
          {statusLabels[row.original.status] || row.original.status}
        </Badge>
      ),
      size: 100,
    },
    createSortableColumn<QCRow>("updatedAt", "ວັນທີ່ອັບເດດລ່າສຸດ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTimeLocal(row.original.updatedAt)}
        </span>
      ),
    }),
    {
      size: 100,
      id: "actions",
      cell: ({ row }: { row: { original: QCRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has(Permissions.qc.read) && onView) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original.id),
          });
        }

        if (
          has(Permissions.qc_record.read) &&
          has(Permissions.qc_record.create) &&
          onRecordClick
        ) {
          actions.push({
            label: "ກວດສອບ",
            icon: <ClipboardCheckIcon className="h-4 w-4" />,
            onClick: () => onRecordClick(row.original),
          });
        }

        if (has(Permissions.qc.update) && onEdit) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }

        if (has(Permissions.qc.update)) {
          const isActive = row.original.status === "active";
          if (isActive && onDeactivate) {
            actions.push({
              label: "ປິດໃຊ້ງານ",
              icon: <EyeOffIcon className="h-4 w-4" />,
              onClick: () => onDeactivate(row.original),
            });
          } else if (!isActive && onActivate) {
            actions.push({
              label: "ເປີດໃຊ້ງານ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: () => onActivate(row.original),
            });
          }
        }

        if (has(Permissions.qc.create) && onCopy) {
          actions.push({
            label: "ຄັດລອກ",
            icon: <CopyIcon className="h-4 w-4" />,
            onClick: () => onCopy(row.original.id),
          });
        }

        if (has(Permissions.qc.delete) && onDelete) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const qc = row.original;
              const ok = await confirm({
                title: "ລຶບມາດຕະຖານ QC",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ "${qc.name}"? ການກະທໍານີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
                actionText: "ລຶບ",
                cancelText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onDelete(qc);
              }
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <div data-tourid="qc-table">
      <DataTable<QCRow, unknown>
        noDataMessage="ບໍ່ພົບມາດຕະຖານ QC"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </div>
  );
}
